import type { Ref } from "react";
import classNames from "classnames";
import styles from "./LiveDot.module.scss";

/** md: a 16px box with the dot; xs: a 4px dot in a 4px box. */
export type LiveDotSize = "md" | "xs";

/** neutral: the 6px gray dot; accent: the side nav's 5px blue dot. */
export type LiveDotVariant = "neutral" | "accent";

interface LiveDotProps {
    /** @default "md" */
    size?: LiveDotSize;
    /** @default "neutral" */
    variant?: LiveDotVariant;
    /** Read out as an image; without it the dot is decorative. */
    label?: string;
    className?: string;
    ref?: Ref<HTMLSpanElement>;
}

/**
 * Dot that says something is happening right now by pulsing its opacity; its
 * size never changes. md is a 16px box; xs takes only the dot's box.
 */
export const LiveDot = ({
    size = "md",
    variant = "neutral",
    label,
    className,
    ref,
}: Readonly<LiveDotProps>) => (
    <span
        ref={ref}
        className={classNames(
            styles.liveDot,
            variant === "accent" && styles.accent,
            size === "xs" && styles.xs,
            className,
        )}
        {...(label === undefined
            ? { "aria-hidden": true }
            : { role: "img", "aria-label": label })}
    />
);
